import { Count } from "@/app/collections/view/count"
import type { LeaderboardItem } from "../../../store.reatom"
import { ChangeTrend } from "../../change-trend"
import { Chart, ChartSkeleton } from "../../chart-client-only"
import { CollectionLink } from "../../collection-link"
import {
  CollectionPreview,
  CollectionPreviewSkeleton,
} from "../../collection-preview"
import { Price } from "../../price"
import {
  Cell,
  CellSkeleton,
  HeaderCell,
  StickyCell,
  StickyHeaderCell,
} from "../cells"
import { ColumnHeader } from "./column-header"

enum ColumnType {
  Collection = "collection",
  Floor = "floor",
  FloorChange = "floorChange",
  TopOffer = "topOffer",
  Sales = "sales",
  Owners = "owners",
  Volume = "volume",
  FloorChart = "floor chart",
}

interface Column {
  type: ColumnType
  Header: React.ComponentType
  Cell: React.ComponentType<{ children: React.ReactNode }>
  Content: React.ComponentType<LeaderboardItem>
  Skeleton: React.ComponentType
}

export function getColumns(
  isHolding = false,
  showBlockchain = false,
  hideFloorChart = false,
): Column[] {
  const columns: Column[] = [
    {
      type: ColumnType.Collection,
      Header: () => (
        <StickyHeaderCell>
          <ColumnHeader type={ColumnType.Collection} />
        </StickyHeaderCell>
      ),
      Cell: StickyCell,
      Content: (item) => (
        <CollectionLink
          collectionId={item.id}
          isHolding={isHolding}
          className="flex items-center justify-start gap-x-2"
        >
          <CollectionPreview
            collectionId={item.id}
            name={item.name}
            image={item.image}
            blockchain={item.blockchain}
            showBlockchain={showBlockchain}
            verified={item.verified}
            drop={item.drop}
            statistics={item.statistics}
          />
        </CollectionLink>
      ),
      Skeleton: CollectionPreviewSkeleton,
    },
    {
      type: ColumnType.Floor,
      Header: () => (
        <HeaderCell>
          <ColumnHeader type={ColumnType.Floor} />
        </HeaderCell>
      ),
      Cell,
      Content: (item) => (
        <CollectionLink collectionId={item.id} isHolding={isHolding}>
          {item.statistics?.floor?.value ? (
            <Price price={item.statistics.floor.value} />
          ) : (
            <UndefinedLabel />
          )}
        </CollectionLink>
      ),
      Skeleton: CellSkeleton,
    },
    {
      type: ColumnType.FloorChange,
      Header: () => (
        <HeaderCell>
          <ColumnHeader type={ColumnType.FloorChange} />
        </HeaderCell>
      ),
      Cell,
      Content: (item) => (
        <CollectionLink collectionId={item.id} isHolding={isHolding}>
          {/* 0 is a real change (an unchanged floor), shown as 0% */}
          {item.statistics?.floor?.value &&
          typeof item.statistics.floor.changePercent === "number" ? (
            <ChangeTrend
              className="justify-end"
              changePercent={item.statistics.floor.changePercent}
            />
          ) : (
            <UndefinedLabel />
          )}
        </CollectionLink>
      ),
      Skeleton: CellSkeleton,
    },
    {
      type: ColumnType.TopOffer,
      Header: () => (
        <HeaderCell>
          <ColumnHeader type={ColumnType.TopOffer} />
        </HeaderCell>
      ),
      Cell,
      Content: (item) => (
        <CollectionLink collectionId={item.id} isHolding={isHolding}>
          {item.statistics?.topOffer ? (
            <Price price={item.statistics.topOffer.value} />
          ) : (
            <UndefinedLabel />
          )}
        </CollectionLink>
      ),
      Skeleton: CellSkeleton,
    },
    {
      type: ColumnType.Sales,
      Header: () => (
        <HeaderCell>
          <ColumnHeader type={ColumnType.Sales} />
        </HeaderCell>
      ),
      Cell,
      Content: (item) => (
        <CollectionLink collectionId={item.id} isHolding={isHolding}>
          <Count className="text-right" value={item.statistics.sales} />
        </CollectionLink>
      ),
      Skeleton: CellSkeleton,
    },
    {
      type: ColumnType.Owners,
      Header: () => (
        <HeaderCell>
          <ColumnHeader type={ColumnType.Owners} />
        </HeaderCell>
      ),
      Cell,
      Content: (item) => (
        <CollectionLink collectionId={item.id} isHolding={isHolding}>
          <Count className="text-right" value={item.statistics.owners} />
        </CollectionLink>
      ),
      Skeleton: CellSkeleton,
    },
    {
      type: ColumnType.Volume,
      Header: () => (
        <HeaderCell>
          <ColumnHeader type={ColumnType.Volume} />
        </HeaderCell>
      ),
      Cell,
      Content: (item) => (
        <CollectionLink collectionId={item.id} isHolding={isHolding}>
          {item.statistics.volume ? (
            <Price price={item.statistics.volume.value} />
          ) : (
            <UndefinedLabel />
          )}
        </CollectionLink>
      ),
      Skeleton: CellSkeleton,
    },
    {
      type: ColumnType.FloorChart,
      Header: () => (
        <HeaderCell>
          <ColumnHeader type={ColumnType.FloorChart} />
        </HeaderCell>
      ),
      Cell,
      Content: (item) => (
        <CollectionLink collectionId={item.id} isHolding={isHolding}>
          {item.statistics.floor?.chartData.length &&
          item.statistics.floor.chartData.length > 1 ? (
            <Chart chartData={item.statistics.floor.chartData} />
          ) : (
            <UndefinedLabel />
          )}
        </CollectionLink>
      ),
      Skeleton: ChartSkeleton,
    },
  ]

  // Solana has no floor history to chart: its floors mostly come from other
  // marketplaces, which publish none.
  return hideFloorChart
    ? columns.filter((column) => column.type !== ColumnType.FloorChart)
    : columns
}

function UndefinedLabel() {
  return <span>—</span>
}
